import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { FDStack } from '@facet-wealth/design';
import { HouseholdMemberList } from 'Components/ComponentLibrary/Organisms/HouseholdMemberList/HouseholdMemberList';
import { ManageHouseholdMember } from 'Views/Page/Content/Templates/ProfilePageMDC/ManageHouseholdMember';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';

export const ManageHouseholdMembers = ({
  membersCollection,
  refreshMembers,
  onAddMember,
  addMemberRelationship,
}) => {
  const isMobile = useMobileLayout();
  const [selectedMember, setSelectedMember] = React.useState(isMobile ? null : membersCollection.resources().head());
  const { loadModalCollectionByLink, clearModalCollection } = useModalContext();

  const onDeleteMember = (_, memberToDelete) => {
    refreshMembers();

    // Select someone new to view on desktop if you clear out a member
    if (!isMobile) {
      const newMemberResource = membersCollection.resources().list().find((res) => {
        return res.type() !== memberToDelete.type() || res.id() !== memberToDelete.id();
      });

      setSelectedMember(newMemberResource);
    }
  };

  const onClick = React.useCallback((resource) => {
    if (isMobile) {
      return resource.withRelationship(fr.relationships.details.name, (rel) => {
        loadModalCollectionByLink(rel.link('related').url());
      }, () => {
        return null;
      });
    }

    // Just set the member into state on desktop so they load off to the right
    setSelectedMember(resource);
    return undefined;
  }, [isMobile]);

  React.useEffect(() => {
    if (isMobile) {
      setSelectedMember(null); // unselect
    } else {
      clearModalCollection();
    }
  }, [isMobile]);

  const isSelected = React.useCallback((resource) => {
    if (!selectedMember) {
      return false;
    }
    return resource.id() === selectedMember.id() && resource.type() === selectedMember.type();
  }, [selectedMember]);

  return (
    <FDStack alignItems={'flex-start'}>
      <HouseholdMemberList
        onClick={onClick}
        collection={membersCollection}
        isSelected={isSelected}
        onAddMember={onAddMember}
        addMemberRelationship={addMemberRelationship}
      />
      <ManageHouseholdMember
        selectedResource={selectedMember}
        onDeleteMember={onDeleteMember}
      />
    </FDStack>
  );
};

ManageHouseholdMembers.defaultProps = {
  addMemberRelationship: null,
  onAddMember: () => {},
};

ManageHouseholdMembers.propTypes = {
  membersCollection: PropTypes.instanceOf(fr.Collection).isRequired,
  refreshMembers: PropTypes.func.isRequired,
  addMemberRelationship: PropTypes.object,
  onAddMember: PropTypes.func,
};
